import React, { createContext, useState, useContext, useEffect } from 'react';
import { auth as authApi } from '../services/api';
import api from '../services/api';
import { toast } from 'react-toastify';
import { PLATFORM_ROLE, persistPlatformSession } from '../utils/landing';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Permissões por defeito (tudo ativo)
const DEFAULT_PERMISSIONS = {
  canAccessAITrainer: true,
  canAccessBookings: true,
  canAccessClasses: true,
  canAccessWorkoutPlans: true,
  canAccessMealPlans: true,
  canAccessGamification: true,
  canAccessTokenShop: true,
  canAccessMessages: true,
  canAccessCredits: true,
  canAccessSmartwatch: true,
  canAccessNotifications: true,
  // Gerir não é "acesso": só quem o recebeu de propósito (um Admin, ou um perfil com a permissão).
  canManageExerciseLibrary: false,
  // Idem para os planos de treino: um Admin, ou um perfil com "Gerir planos de treino" (o servidor valida sempre).
  canManageWorkoutPlans: false,
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [permissions, setPermissions] = useState(DEFAULT_PERMISSIONS);
  const [loading, setLoading] = useState(true);
  // As permissões chegam do servidor depois de o utilizador existir. Uma rota protegida por uma
  // permissão de gestão (que por omissão é falsa) tem de esperar por elas: sem isto, um treinador com
  // a permissão que recarregasse a página era mandado para "/" antes de a resposta chegar.
  const [permissionsLoading, setPermissionsLoading] = useState(() => {
    try { return !!localStorage.getItem('token'); } catch { return false; }
  });

  useEffect(() => {
    // Clear any potentially corrupted localStorage data first
    try {
      const token = localStorage.getItem('token');
      const userData = localStorage.getItem('user');
      
      // Only proceed if both exist and userData looks like JSON
      if (token && userData && userData.length > 0) {
        // Additional validation - must start and end with braces and be reasonable length
        if (userData.startsWith('{') && userData.endsWith('}') && userData.length > 10 && userData.length < 10000) {
          try {
            const parsedUser = JSON.parse(userData);
            // Validate the parsed object has expected properties
            if (parsedUser && typeof parsedUser === 'object' && parsedUser.role === PLATFORM_ROLE) {
              // Uma sessão antiga de plataforma guardada como sessão do portal (o login devolvia-a): o portal é do ginásio, a plataforma tem a sua consola.
              persistPlatformSession(token, parsedUser);
            } else if (parsedUser && typeof parsedUser === 'object' && parsedUser.email) {
              setUser(parsedUser);
            } else {
              console.warn('Invalid user object structure, clearing localStorage');
              localStorage.clear();
            }
          } catch (parseError) {
            console.error('JSON parse error, clearing all localStorage:', parseError);
            localStorage.clear();
          }
        } else {
          console.warn('Invalid user data format, clearing localStorage');
          localStorage.clear();
        }
      } else if (userData && userData.length > 0) {
        // userData exists but looks invalid
        console.warn('Potentially corrupted user data detected, clearing localStorage');
        localStorage.clear();
      }
    } catch (error) {
      console.error('Error in auth initialization, clearing localStorage:', error);
      localStorage.clear();
    }
    
    setLoading(false);
  }, []);

  // Carregar permissões quando o user muda
  const loadPermissions = async () => {
    setPermissionsLoading(true);
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setPermissions(DEFAULT_PERMISSIONS);
        return;
      }
      const response = await api.get('/auth/my-permissions');
      setPermissions(response.data);
      localStorage.setItem('permissions', JSON.stringify(response.data));
    } catch (error) {
      console.error('Erro ao carregar permissões:', error);
      // Tentar do localStorage
      try {
        const stored = localStorage.getItem('permissions');
        if (stored) setPermissions(JSON.parse(stored));
      } catch (e) {
        setPermissions(DEFAULT_PERMISSIONS);
      }
    } finally {
      setPermissionsLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      loadPermissions();
    } else {
      setPermissions(DEFAULT_PERMISSIONS);
      // Este ramo corre também no arranque, antes de o utilizador ser restaurado do localStorage: com um
      // token guardado há permissões por carregar, e dizer o contrário aqui mandava um treinador com a
      // permissão para "/" ao recarregar a página (as de gestão são falsas até chegarem).
      try {
        if (!localStorage.getItem('token')) setPermissionsLoading(false);
      } catch {
        setPermissionsLoading(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const hasPermission = (permissionKey) => {
    if (!user) return false;
    if (user.role === 'Admin') return true;
    // As permissões de acesso a funcionalidades são "tudo ligado" até alguém as desligar; as de
    // gestão (canManage…) são o contrário: se a resposta não as trouxer (um servidor mais antigo),
    // não se concedem.
    if (permissionKey.startsWith('canManage')) return permissions[permissionKey] === true;
    return permissions[permissionKey] ?? true;
  };

  const login = async (email, password) => {
    try {
      const response = await authApi.login(email, password);

      // Operador da plataforma: não tem sessão neste ginásio. O servidor diz onde entrar (o portal) e não emite token nenhum.
      if (response.data?.platformRedirect) {
        toast.info('A abrir o portal de plataforma…');
        return { platformRedirect: response.data.platformRedirect };
      }

      const { token, user } = response.data;

      // Conta só da plataforma (sem utilizador neste ginásio): não é uma sessão do portal; a consola da plataforma é que a usa.
      if (user?.role === PLATFORM_ROLE) {
        persistPlatformSession(token, user);
        toast.success('Login efetuado com sucesso!');
        return 'platform';
      }

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      setPermissionsLoading(true);   // as permissões deste utilizador ainda não chegaram
      setUser(user);

      toast.success('Login efetuado com sucesso!');
      return true;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao efetuar login');
      return false;
    }
  };

  const register = async (name, email, password, phone) => {
    try {
      const response = await authApi.register(name, email, password, phone);
      const { token, user } = response.data;

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));
      setPermissionsLoading(true);
      setUser(user);

      toast.success('Registo efetuado com sucesso!');
      return true;
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao efetuar registo');
      return false;
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('permissions');
    // O service worker guarda respostas da API por endereço (treino de hoje, perfil…), sem saber de quem são: num aparelho partilhado,
    // o sócio seguinte, sem rede, receberia as do anterior. Sai com a sessão (as imagens e a app em si ficam: não são de ninguém).
    try {
      if (typeof caches !== 'undefined') caches.keys().then((keys) => keys.filter((k) => k.startsWith('gymag-dynamic-')).forEach((k) => caches.delete(k)));
    } catch { /* sem Cache API: nada a limpar */ }
    setUser(null);
    setPermissions(DEFAULT_PERMISSIONS);
    toast.info('Sessão terminada');
  };

  const updateUser = (updatedUserData) => {
    const newUser = { ...user, ...updatedUserData };
    localStorage.setItem('user', JSON.stringify(newUser));
    setUser(newUser);
  };

  // O servidor recusou algo por a password ser temporária (ex.: sessão que já estava aberta): passa ao ecrã de troca.
  useEffect(() => {
    const onRequired = () => setUser((u) => {
      if (!u || u.mustChangePassword) return u;
      const next = { ...u, mustChangePassword: true };
      try { localStorage.setItem('user', JSON.stringify(next)); } catch { /* sem armazenamento */ }
      return next;
    });
    window.addEventListener('gymag:password-change-required', onRequired);
    return () => window.removeEventListener('gymag:password-change-required', onRequired);
  }, []);

  // A password foi trocada: guarda o token novo (sem a marca de temporária) e deixa a aplicação abrir.
  const completePasswordChange = (newToken) => {
    if (newToken) localStorage.setItem('token', newToken);
    const next = { ...(user || {}), mustChangePassword: false };
    localStorage.setItem('user', JSON.stringify(next));
    setUser(next);
  };

  const isAdmin = user?.role === 'Admin';
  const isClient = user?.role === 'Client';

  const value = {
    user,
    login,
    register,
    logout,
    updateUser,
    completePasswordChange,
    isAdmin,
    isClient,
    loading,
    permissions,
    permissionsLoading,
    hasPermission,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
